import { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

interface AnimatedBadgeProps {
  /** Badge image URL (e.g. /uploads/badges/Daiv_Badge.webp). */
  src: string;
  /** Accent glow colour. */
  glow: string;
  /** Rendered diameter in px. */
  size?: number;
  /** `full` = float + glow + shimmer + sparkle (profile). `subtle` = glow pulse only (navbar). */
  variant?: 'full' | 'subtle';
  alt?: string;
  className?: string;
}

/**
 * Loyalty badge rendered as a floating, glowing coin. The baked-in dark
 * background of the artwork is hidden via `mix-blend-mode: screen` against a
 * dark circular disc, so it reads cleanly on both light and dark themes with
 * no transparency/PNG needed. Animation is disabled for reduced-motion users
 * and mount-gated to avoid a first-paint flash.
 */
export default function AnimatedBadge({
  src,
  glow,
  size = 88,
  variant = 'full',
  alt = '',
  className = '',
}: AnimatedBadgeProps) {
  const { isDark } = useTheme();
  const reduce = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const animate = mounted && !reduce;
  const full = variant === 'full';

  const glowBlur = Math.round(size * 0.3);

  return (
    <div
      className={`relative shrink-0 ${className}`}
      style={{ width: size, height: size }}
      aria-label={alt || undefined}
    >
      {/* coin + float */}
      <motion.div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle at 50% 42%, #0a0f0d, #05080a)',
          boxShadow: `0 0 0 1px ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.10)'}, 0 ${Math.round(size * 0.06)}px ${Math.round(size * 0.18)}px rgba(0,0,0,0.45)`,
        }}
        animate={animate && full ? { y: [0, -size * 0.05, 0] } : undefined}
        transition={{ duration: 3.6, repeat: Infinity, ease: 'easeInOut' }}
      >
        {/* glow halo */}
        <motion.div
          className="absolute rounded-full pointer-events-none"
          style={{
            inset: -Math.round(size * 0.04),
            boxShadow: `0 0 ${glowBlur}px 1px ${glow}, inset 0 0 ${Math.round(glowBlur * 0.6)}px -4px ${glow}`,
          }}
          animate={animate ? { opacity: [0.45, 0.95, 0.45] } : undefined}
          transition={{ duration: full ? 2.6 : 3.2, repeat: Infinity, ease: 'easeInOut' }}
          initial={{ opacity: 0.6 }}
        />

        {/* badge art — screen blend drops the baked black background */}
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="absolute inset-0 w-full h-full rounded-full object-cover select-none"
          style={{ mixBlendMode: 'screen', transform: 'scale(1.02)' }}
        />

        {full && (
          <>
            {/* shimmer sweep */}
            <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none">
              <motion.div
                className="absolute"
                style={{
                  top: '-40%',
                  width: '55%',
                  height: '180%',
                  background:
                    'linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)',
                  filter: 'blur(2px)',
                  rotate: '8deg',
                }}
                animate={animate ? { left: ['-60%', '120%'] } : undefined}
                transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.2 }}
                initial={{ left: '-60%' }}
              />
            </div>

            {/* twinkling sparkle */}
            <motion.div
              className="absolute pointer-events-none"
              style={{
                right: size * 0.07,
                bottom: size * 0.07,
                width: size * 0.12,
                height: size * 0.12,
                background: `radial-gradient(circle, #fff 0 12%, transparent 13%), conic-gradient(from 0deg, transparent 0 42%, ${glow} 50%, transparent 58% 92%, ${glow} 100%)`,
                WebkitMaskImage: 'radial-gradient(circle at center, #000 60%, transparent 62%)',
                maskImage: 'radial-gradient(circle at center, #000 60%, transparent 62%)',
              }}
              animate={animate ? { opacity: [0, 1, 0], scale: [0.3, 1, 0.3], rotate: [0, 90, 0] } : undefined}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              initial={{ opacity: 0 }}
            />
          </>
        )}
      </motion.div>
    </div>
  );
}
